import React, { useMemo, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const RegistrationForm = ({ onSubmit, onQuickAuth, isFull, session, paymentInfo, useFixedSubmitBar = true, useCardWrapper = true, showQuickAuthSection = true, showTitle = true, useStickySubmitBarOnMobile = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    skillLevel: '',
    guestCount: '0',
    agreeTerms: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  const summaryRef = useRef(null);

  const formattedSessionDateTime = useMemo(() => {
    if (!session?.date) return '';
    const date = new Date(session?.date);
    const dateText = date?.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'short'
    });
    const timeText = session?.startTime && session?.endTime ? `${session?.startTime} - ${session?.endTime}` : '';
    return `${dateText}${timeText ? ` / ${timeText}` : ''}`;
  }, [session?.date, session?.startTime, session?.endTime]);

  const paymentSummaryText = useMemo(() => {
    if (!paymentInfo) return '';
    const bankLine = paymentInfo?.bankName && paymentInfo?.accountNumber
      ? `${paymentInfo?.bankName} ${paymentInfo?.accountNumber}`
      : '';
    const holderLine = paymentInfo?.accountHolder ? `예금주: ${paymentInfo?.accountHolder}` : '';
    return [bankLine, holderLine].filter(Boolean).join('\n');
  }, [paymentInfo]);

  const skillLevelOptions = [
    { value: '', label: '실력 수준 선택', disabled: true },
    { value: 'beginner', label: '초급 (1.0-2.5)' },
    { value: 'intermediate', label: '중급 (3.0-3.5)' },
    { value: 'advanced', label: '고급 (4.0-4.5)' },
    { value: 'expert', label: '최상급 (5.0+)' }
  ];

  const validatePhone = (phone) => {
    const phoneRegex = /^01[0-9]-?[0-9]{3,4}-?[0-9]{4}$/;
    return phoneRegex?.test(phone?.replace(/\s/g, ''));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handlePhoneChange = (e) => {
    const value = e?.target?.value?.replace(/[^\d]/g, '');
    let formatted = value;
    
    if (value?.length <= 3) {
      formatted = value;
    } else if (value?.length <= 7) {
      formatted = `${value?.slice(0, 3)}-${value?.slice(3)}`;
    } else {
      formatted = `${value?.slice(0, 3)}-${value?.slice(3, 7)}-${value?.slice(7, 11)}`;
    }
    
    handleInputChange('phone', formatted);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = '이름을 입력해주세요';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = '전화번호를 입력해주세요';
    } else if (!validatePhone(formData?.phone)) {
      newErrors.phone = '올바른 전화번호 형식이 아닙니다 (예: 010-1234-5678)';
    }

    if (!formData?.skillLevel) {
      newErrors.skillLevel = '실력 수준을 선택해주세요';
    }

    if (!formData?.agreeTerms) {
      newErrors.agreeTerms = '약관에 동의해주세요';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!isSummaryExpanded) {
      setIsSummaryExpanded(true);
      setTimeout(() => {
        summaryRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit(formData);
    } catch (error) {
      setErrors({ submit: error?.message || '신청 중 오류가 발생했습니다' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuickAuth = () => {
    onQuickAuth();
  };

  return (
    <div className={useCardWrapper ? "bg-card border border-border rounded-xl p-4 md:p-6 lg:p-8 shadow-md" : "p-0"}>
      {showTitle && (
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground">
            참가신청
          </h3>
        </div>
      )}
      {showQuickAuthSection && (
        <>
          <div className="mb-6">
            <Button
              variant="outline"
              iconName="Zap"
              iconPosition="left"
              onClick={handleQuickAuth}
              fullWidth
            >
              기존 회원 빠른 인증
            </Button>
            <p className="text-xs md:text-sm caption text-muted-foreground mt-2 text-center">
              전화번호로 빠르게 신청하기
            </p>
          </div>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="bg-card px-4 text-sm caption text-muted-foreground">
                또는 신규 신청
              </span>
            </div>
          </div>
        </>
      )}
      <form
        onSubmit={handleSubmit}
        className={`space-y-4 md:space-y-6 ${useFixedSubmitBar ? 'pb-24 md:pb-0' : ''} ${useStickySubmitBarOnMobile ? 'pb-24 md:pb-0' : ''}`}
      >
        <Input
          label="이름"
          type="text"
          placeholder="홍길동"
          value={formData?.name}
          onChange={(e) => handleInputChange('name', e?.target?.value)}
          error={errors?.name}
          required
          disabled={isSubmitting}
        />

        <Input
          label="전화번호"
          type="tel"
          placeholder="010-1234-5678"
          value={formData?.phone}
          onChange={handlePhoneChange}
          error={errors?.phone}
          required
          disabled={isSubmitting}
          description="신청 확인 및 알림 전송에 사용됩니다"
        />

        <Select
          label="실력 수준"
          options={skillLevelOptions}
          value={formData?.skillLevel}
          onChange={(value) => handleInputChange('skillLevel', value)}
          error={errors?.skillLevel}
          required
          disabled={isSubmitting}
          description="매칭을 위해 정확한 실력 수준을 선택해주세요"
        />

        <div className="pt-4 border-t border-border">
          <Checkbox
            label="개인정보 수집 및 이용에 동의합니다"
            description="신청 처리 및 세션 운영을 위해 필요한 최소한의 정보만 수집합니다"
            checked={formData?.agreeTerms}
            onChange={(e) => handleInputChange('agreeTerms', e?.target?.checked)}
            error={errors?.agreeTerms}
            required
            disabled={isSubmitting}
          />
        </div>

        {errors?.submit && (
          <div className="bg-error/10 border border-error/20 rounded-md p-3 md:p-4">
            <div className="flex items-start gap-2">
              <Icon name="AlertCircle" size={18} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
              <p className="text-sm text-error">{errors?.submit}</p>
            </div>
          </div>
        )}

        {(session || paymentInfo) && (
          <div ref={summaryRef} className="bg-muted rounded-md border border-border p-3 md:p-4">
            <button
              type="button"
              onClick={() => setIsSummaryExpanded(v => !v)}
              className="w-full flex items-start justify-between gap-3 text-left"
            >
              <div className="flex-1">
                <p className="text-sm font-heading font-medium text-foreground">참가신청 정보</p>
                <p className="text-xs caption text-muted-foreground mt-1">
                  {isSummaryExpanded ? '닫기' : '펼치기'}
                </p>
              </div>
              <div className="mt-0.5 text-muted-foreground">
                <Icon name={isSummaryExpanded ? 'ChevronUp' : 'ChevronDown'} size={18} />
              </div>
            </button>

            {isSummaryExpanded && (
              <div className="mt-3 space-y-3">
                {session && (
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-foreground">{session?.title}</p>
                    {(session?.venue || session?.location) && (
                      <p className="text-xs caption text-muted-foreground">
                        {session?.venue}{session?.location ? ` · ${session?.location}` : ''}
                      </p>
                    )}
                    {formattedSessionDateTime && (
                      <p className="text-xs caption text-muted-foreground">{formattedSessionDateTime}</p>
                    )}
                    {session?.skillRange && (
                      <p className="text-xs caption text-muted-foreground">실력: {session?.skillRange}</p>
                    )}
                    {typeof session?.capacity === 'number' && typeof session?.confirmed === 'number' && (
                      <p className="text-xs caption text-muted-foreground">
                        정원: {session?.confirmed}/{session?.capacity}
                      </p>
                    )}
                  </div>
                )}

                {paymentInfo && (
                  <div className="space-y-1">
                    <p className="text-sm font-heading font-medium text-foreground">결제 안내</p>
                    {paymentSummaryText && (
                      <pre className="text-xs caption text-muted-foreground whitespace-pre-wrap leading-relaxed">
                        {paymentSummaryText}
                      </pre>
                    )}
                    {paymentInfo?.notes && (
                      <pre className="text-xs caption text-muted-foreground whitespace-pre-wrap leading-relaxed">
                        {paymentInfo?.notes}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {useFixedSubmitBar ? (
          <div className="fixed inset-x-0 bottom-0 z-200 bg-background/95 backdrop-blur border-t border-border p-4 md:static md:z-auto md:bg-transparent md:backdrop-blur-none md:border-0 md:p-0">
            <Button
              type="submit"
              variant="default"
              iconName="CheckCircle"
              iconPosition="right"
              loading={isSubmitting}
              fullWidth
            >
              신청하기
            </Button>
          </div>
        ) : useStickySubmitBarOnMobile ? (
          <div className="sticky bottom-0 -mx-4 md:mx-0 bg-card/95 backdrop-blur border-t border-border pt-4 px-4 pb-[calc(env(safe-area-inset-bottom)+16px)] md:static md:bg-transparent md:backdrop-blur-none md:border-0 md:p-0">
            <Button
              type="submit"
              variant="default"
              iconName="CheckCircle"
              iconPosition="right"
              loading={isSubmitting}
              fullWidth
            >
              신청하기
            </Button>
          </div>
        ) : (
          <Button
            type="submit"
            variant="default"
            iconName="CheckCircle"
            iconPosition="right"
            loading={isSubmitting}
            fullWidth
          >
            신청하기
          </Button>
        )}
      </form>
    </div>
  );
};

export default RegistrationForm;